import "react-phone-input-2/lib/style.css";
import { useAppSelector } from "store/hooks";
import PhoneInput from "react-phone-input-2";
import { userSettingState } from "store/selectors";
import { NotificationManager } from "react-notifications";
import { InputHTMLAttributes, useEffect, useRef, useState } from "react";
import { EyeIcon, EyeOffIcon, ImportIcon } from "assets/svgs";
import examples from "libphonenumber-js/examples.mobile.json";
import { CountryCode } from "libphonenumber-js/types";
import parsePhoneNumberFromString, {
  getCountryCallingCode,
  getExampleNumber,
} from "libphonenumber-js";
import { Button } from "components/ui";
import styles from "./styles.module.css";

function getPhonePlaceholder(country: CountryCode) {
  const example = getExampleNumber(country, examples) || "";
  return example ? example.formatInternational() : "";
}

function getPhoneCode(country: CountryCode) {
  const dialCode = getCountryCallingCode(country) || "";
  return `+${dialCode}`;
}

// interface CustomInputProps extends InputHTMLAttributes<HTMLInputElement> {
//   errorMessage?: string;
//   classes?: string;
//   value: any;
// }

interface CustomInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  errorMessage?: string;
  classes?: string;
  value: any;
  onChange: (value: string, meta?: any) => void;
}

export function CustomInputWithErrorMessage({
  errorMessage,
  classes,
  onChange,
  ...props
}: CustomInputProps) {
  const { isDarkMode } = useAppSelector(userSettingState);
  return (
    <div className={styles.inputContainer}>
      <input
        {...props}
        className={`${styles.customInput} ${classes} ${
          isDarkMode ? styles.darkMode : ""
        } ${errorMessage ? styles.errorInputBorder : ""} `}
        onChange={(e: any) => onChange(e.target.value)}
      />
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
    </div>
  );
}

export function CustomInput({ classes, onChange, ...props }: any) {
  const { isDarkMode } = useAppSelector(userSettingState);
  return (
    <input
      {...props}
      className={`${styles.customInput} ${classes}  ${
        isDarkMode ? styles.darkMode : ""
      }`}
      onChange={(e: any) => onChange(e.target.value)}
    />
  );
}

export function CustomFileInput({ classes, onChange, ...props }: any) {
  const { isDarkMode } = useAppSelector(userSettingState);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (file) {
      if (file.size > 1000 * 1024) {
        return NotificationManager.warning(
          "Document must be less than 1 MB",
          "",
          10000
        );
      }
      onChange(file);
    }
  };

  return (
    <>
      <div
        className={`${styles.customInput} ${classes}  ${
          isDarkMode ? styles.darkMode : ""
        } p-0 flex gap-2 items-center`}
      >
        <Button
          variant="green"
          className="flex gap-2 h-full items-center rounded-r-none p-3"
          title="Upload File"
          type="button"
          onClick={() => {
            fileInputRef.current.click();
          }}
        >
          <ImportIcon className="w-4  fill-white" />
          File
        </Button>
        <span>{props.value}</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        accept=".pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt"
        style={{ display: "none" }}
        hidden
      />
    </>
  );
}

export function Checkbox({ classes, onChange, ...props }: any) {
  return (
    <input
      type="checkbox"
      {...props}
      className={`${classes} ${styles.customCheckbox}`}
      onChange={(e: any) => onChange(e.target.checked)}
    />
  );
}

export function TextArea({ classes, onChange, ...props }: any) {
  const { isDarkMode } = useAppSelector(userSettingState);
  return (
    <textarea
      {...props}
      className={`${styles.customInput} ${classes} ${
        isDarkMode ? styles.darkMode : ""
      }`}
      onChange={(e: any) => onChange(e.target.value)}
    />
  );
}

export function PasswordInput({ classes, onChange, eyeDark, ...props }: any) {
  const [showPassword, setShowPassword] = useState(false);
  const { isDarkMode } = useAppSelector(userSettingState);

  return (
    <div
      className={
        isDarkMode
          ? styles.passwordInputContainerDark
          : styles.passwordInputContainer
      }
    >
      <input
        type={showPassword ? "text" : "password"}
        className={`${styles.passwordInput} ${classes}`}
        placeholder="Enter your password"
        {...props}
        onChange={(e: any) => onChange(e.target.value)}
      />
      <button
        type="button"
        className={styles.eyeBtn}
        onClick={() => setShowPassword(!showPassword)}
      >
        {showPassword ? (
          <EyeOffIcon className={`${eyeDark ? "fill-mildGray" : ""} w-5 h-5`} />
        ) : (
          <EyeIcon className={`${eyeDark ? "fill-mildGray" : ""} w-5 h-5`} />
        )}
      </button>
    </div>
  );
}

export function RadioButton({ classes, onChange, id, ...props }: any) {
  return (
    <label htmlFor={id} className={styles.radioLabel}>
      <input
        type="radio"
        id={id}
        {...props}
        className={`${classes} ${styles.customRadio}`}
        onChange={(e: any) => onChange(e.target.value)}
      />
      {props.value}
    </label>
  );
}

export function PhoneInputField({
  classes,
  onChange,
  id,
  codeEditable = false,
  ...props
}: any) {
  const { isDarkMode } = useAppSelector(userSettingState);
  const classesWrapper = `${
    props?.disabled ? "pointer-events-none" : ""
  } ${classes} ${styles.customInput} ${
    isDarkMode ? styles.phoneInputDark : styles.phoneInput
  }`;

  return (
    <PhoneInput
      {...props}
      className={classesWrapper}
      onChange={(phone) => onChange(`+${phone}`)}
      enableSearch
      countryCodeEditable={codeEditable}
      country={"pk"}
    />
  );
}

export interface PhoneInputChangeMeta {
  isValid: boolean;
  code?: string;
}

export interface PhoneInputFieldWithValidationProps {
  value?: string;
  onChange: (value: string, meta: PhoneInputChangeMeta) => void;
  onBlur?: () => void;
  classes?: string;
  codeEditable?: boolean;
  error?: boolean;
  disabled?: boolean;

  // allow passing any other PhoneInput props safely
  [key: string]: any;
}

export function PhoneInputFieldWithValidation({
  classes,
  onChange,
  codeEditable = false,
  error,
  ...props
}: PhoneInputFieldWithValidationProps) {
  const { isDarkMode } = useAppSelector(userSettingState);
  const [country, setCountry] = useState<string>("pk");
  // const [value, setValue] = useState("+92");

  // useEffect(() => {
  //   if (!props?.value) {
  //     setValue(getPhoneCode(country?.toUpperCase() as CountryCode));
  //   }
  // }, [props.value]);

  // useEffect(() => {
  //   if (props?.value) {
  //     setValue(props?.value);
  //   }
  // }, []);

  const classesWrapper = `${
    props?.disabled ? "pointer-events-none" : ""
  } ${classes} ${styles.customInput} ${
    isDarkMode ? styles.phoneInputDark : styles.phoneInput
  } ${error ? "border border-red-500 focus:border-red-500" : ""}`;

  function handleChange(value: string, country: any) {
    const phone = `+${value}`;
    // setValue(phone);
    setCountry(country?.countryCode);

    const phoneNumber = parsePhoneNumberFromString(phone);
    const isValid = phoneNumber?.isValid() && phoneNumber?.isPossible();

    onChange(phone, {
      isValid,
      code: getPhoneCode(country?.countryCode?.toUpperCase() as CountryCode),
    });
  }

  const placeholder =
    country && getPhonePlaceholder(country?.toUpperCase() as CountryCode)
      ? getPhonePlaceholder(country?.toUpperCase() as CountryCode)
      : "Enter phone number";

  const value = props?.value
    ? props?.value
    : getPhoneCode(country.toUpperCase() as CountryCode);

  return (
    <PhoneInput
      {...props}
      inputProps={{
        title: placeholder, // tooltip text
      }}
      value={value}
      containerClass={classesWrapper}
      onChange={handleChange}
      onBlur={props?.onBlur}
      enableSearch
      countryCodeEditable={codeEditable}
      country={country}
    />
  );
}
